/**
 * ラスタ → GL テクスチャの橋渡し。
 *
 * ピクセルの所有者は TextureStore（エンジン共通層・無改造で再利用）、
 * GL オブジェクトの所有者はここ。二重に持たないよう、Texture は「実際に描く面」の分だけ作る。
 */
import { LinearFilter, SRGBColorSpace, Texture } from 'three';
import type { WebGLRenderer } from 'three';
import type { PageRaster, PageRegion } from '../../core/source';
import type { TextureStore } from '../texture-store';

interface Entry {
  raster: PageRaster;
  texture: Texture;
}

export class TextureCache {
  /** 下地（ページ全面）のテクスチャ。 */
  private readonly entries = new Map<number, Entry>();
  /** 拡大時に重ねる領域ラスタのテクスチャ（表示中のページぶんだけ）。 */
  private readonly details = new Map<number, Entry>();

  constructor(
    private readonly renderer: WebGLRenderer,
    private readonly store: TextureStore,
  ) {}

  /** ページのテクスチャを得る。ラスタが差し替わっていれば作り直す。 */
  async get(pageNo: number, targetWidth: number): Promise<Texture> {
    const raster = await this.store.get(pageNo, targetWidth);
    return this.upload(this.entries, pageNo, raster);
  }

  /** 可視領域の高解像度テクスチャ（ラスタが持つ領域は呼び出し側が配置に使う）。 */
  async getDetail(
    pageNo: number,
    targetWidth: number,
    region: PageRegion,
  ): Promise<{ texture: Texture; raster: PageRaster }> {
    const raster = await this.store.getDetail(pageNo, targetWidth, region);
    return { texture: await this.upload(this.details, pageNo, raster), raster };
  }

  private upload(map: Map<number, Entry>, pageNo: number, raster: PageRaster): Texture {
    const cached = map.get(pageNo);
    if (cached && cached.raster === raster) return cached.texture;

    cached?.texture.dispose();

    const texture = new Texture(raster.bitmap);
    texture.colorSpace = SRGBColorSpace;
    texture.minFilter = LinearFilter;
    texture.magFilter = LinearFilter;
    texture.generateMipmaps = false;
    texture.needsUpdate = true;
    // 即時に GPU へ転送する。遅延させると、転送前に TextureStore がラスタを close して
    // 転送元が消える（圏外へ出たページの解放と競合する）。
    this.renderer.initTexture(texture);

    map.set(pageNo, { raster, texture });
    return texture;
  }

  /** 保持するページを指定し、圏外のテクスチャを破棄する（TextureStore.setWindow と対で呼ぶ）。 */
  retain(pages: Iterable<number>): void {
    this.prune(this.entries, new Set(pages));
  }

  /** detail を保持するページを指定する（TextureStore.setDetailWindow と対で呼ぶ）。 */
  retainDetails(pages: Iterable<number>): void {
    this.prune(this.details, new Set(pages));
  }

  /** すべての detail を破棄する（めくり開始時など）。 */
  clearDetails(): void {
    this.prune(this.details, new Set());
  }

  private prune(map: Map<number, Entry>, keep: Set<number>): void {
    for (const [pageNo, entry] of [...map]) {
      if (keep.has(pageNo)) continue;
      entry.texture.dispose();
      map.delete(pageNo);
    }
  }

  /** 保持中のテクスチャ数（ライフサイクルの検証に使う）。 */
  activeCount(): number {
    return this.entries.size + this.details.size;
  }

  dispose(): void {
    for (const entry of this.entries.values()) entry.texture.dispose();
    for (const entry of this.details.values()) entry.texture.dispose();
    this.entries.clear();
    this.details.clear();
  }
}
